import React, {
  createContext,
  useCallback,
  useContext,
  useReducer,
} from "react";

// Initial state for the loading context
const initialState = {
  // Global loading state
  isLoading: false,
  // Map of loading states by key (e.g., 'contacts.getAll', 'deals.create', etc.)
  loadingKeys: {},
  // Request cache to prevent duplicate requests
  requestCache: {},
};

// Create the context
const LoadingContext = createContext(initialState);

// Action types
const SET_LOADING = "SET_LOADING";
const CLEAR_LOADING = "CLEAR_LOADING";
const SET_GLOBAL_LOADING = "SET_GLOBAL_LOADING";
const CLEAR_GLOBAL_LOADING = "CLEAR_GLOBAL_LOADING";
const CACHE_REQUEST = "CACHE_REQUEST";
const CLEAR_REQUEST_CACHE = "CLEAR_REQUEST_CACHE";

// Reducer function to handle loading state updates
const loadingReducer = (state, action) => {
  switch (action.type) {
    case SET_LOADING:
      return {
        ...state,
        loadingKeys: {
          ...state.loadingKeys,
          [action.key]: true,
        },
      };
    case CLEAR_LOADING:
      const updatedLoadingKeys = { ...state.loadingKeys };
      delete updatedLoadingKeys[action.key];
      return {
        ...state,
        loadingKeys: updatedLoadingKeys,
      };
    case SET_GLOBAL_LOADING:
      return {
        ...state,
        isLoading: true,
      };
    case CLEAR_GLOBAL_LOADING:
      return {
        ...state,
        isLoading: false,
      };
    case CACHE_REQUEST:
      return {
        ...state,
        requestCache: {
          ...state.requestCache,
          [action.cacheKey]: {
            promise: action.promise,
            timestamp: Date.now(),
          },
        },
      };
    case CLEAR_REQUEST_CACHE:
      const updatedCache = { ...state.requestCache };
      delete updatedCache[action.cacheKey];
      return {
        ...state,
        requestCache: updatedCache,
      };
    default:
      return state;
  }
};

/**
 * LoadingProvider component to manage global loading state
 */
export const LoadingProvider = ({ children }) => {
  const [state, dispatch] = useReducer(loadingReducer, initialState);

  // Start loading for a specific key
  const startLoading = useCallback((key) => {
    dispatch({ type: SET_LOADING, key });
  }, []);

  // Stop loading for a specific key
  const stopLoading = useCallback((key) => {
    dispatch({ type: CLEAR_LOADING, key });
  }, []);

  // Start global loading
  const startGlobalLoading = useCallback(() => {
    dispatch({ type: SET_GLOBAL_LOADING });
  }, []);

  // Stop global loading
  const stopGlobalLoading = useCallback(() => {
    dispatch({ type: CLEAR_GLOBAL_LOADING });
  }, []);

  // Cache a request with its promise
  const cacheRequest = useCallback((cacheKey, promise) => {
    dispatch({ type: CACHE_REQUEST, cacheKey, promise });
  }, []);

  // Clear a request from cache
  const clearRequestCache = useCallback((cacheKey) => {
    dispatch({ type: CLEAR_REQUEST_CACHE, cacheKey });
  }, []);

  // Get loading state for a specific key
  const isLoadingKey = useCallback(
    (key) => {
      return !!state.loadingKeys[key];
    },
    [state.loadingKeys],
  );

  // Check if any loading is happening (global or any key)
  const isAnyLoading = useCallback(() => {
    return state.isLoading || Object.keys(state.loadingKeys).length > 0;
  }, [state.isLoading, state.loadingKeys]);

  // Get a cached request if it exists and is not expired
  const getCachedRequest = useCallback(
    (cacheKey, maxAge = 5000) => {
      const cachedItem = state.requestCache[cacheKey];
      if (!cachedItem) return null;

      const now = Date.now();
      const age = now - cachedItem.timestamp;

      // Return cached promise if it's not too old
      if (age < maxAge) {
        return cachedItem.promise;
      }

      // Clear expired cache entry
      clearRequestCache(cacheKey);
      return null;
    },
    [state.requestCache, clearRequestCache],
  );

  const value = {
    isLoading: state.isLoading,
    loadingKeys: state.loadingKeys,
    isLoadingKey,
    isAnyLoading,
    startLoading,
    stopLoading,
    startGlobalLoading,
    stopGlobalLoading,
    cacheRequest,
    clearRequestCache,
    getCachedRequest,
  };

  return (
    <LoadingContext.Provider value={value}>{children}</LoadingContext.Provider>
  );
};

// Custom hook to use the loading context
export const useLoadingContext = () => {
  const context = useContext(LoadingContext);
  if (!context) {
    throw new Error("useLoadingContext must be used within a LoadingProvider");
  }
  return context;
};
